import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { StaffTicketDetail } from "@signalist/shared-types";

import { TicketMessage } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketMessage";
import { TicketSystemLine } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketSystemLine";
import { TicketTranscript } from "@/pages/Private/pages/Support/pages/TicketDetail/components/TicketTranscript";
import {
	buildTicketThread,
	ticketUserFirstName,
} from "@/pages/Private/pages/Support/pages/TicketDetail/utils/ticketThread";

import { StaffTicketStatusPill } from "../../../components/StaffTicketPills";

interface StaffTicketThreadProps {
	ticket: StaffTicketDetail;
	onAttachmentError: (fileName: string) => void;
}

/**
 * The full thread as staff see it: the customer thread plus internal notes (decision 2: system
 * lines only for status changes, with the staff pill), body-level files on the first message.
 */
export const StaffTicketThread: FunctionComponent<StaffTicketThreadProps> = ({
	ticket,
	onAttachmentError,
}) => {
	const { t } = useTranslation();
	const items = buildTicketThread(ticket, { includeInternal: true });
	const firstMessageId = items.find((item) => item.kind === "message")?.comment.id;

	return (
		<div className="flex flex-col gap-4" data-testid="staff-thread">
			{items.map((item) => {
				switch (item.kind) {
					case "message":
						return (
							<TicketMessage
								key={item.comment.id}
								comment={item.comment}
								extraAttachments={item.comment.id === firstMessageId ? ticket.attachments : []}
								isInternal={item.isInternal}
								isStaff={item.isStaff}
								onAttachmentError={onAttachmentError}
							/>
						);
					case "transcript":
						return (
							<TicketTranscript
								key={item.comment.id}
								customerName={ticketUserFirstName(ticket.createdBy)}
								entries={item.entries}
							/>
						);
					case "received":
						return (
							<TicketSystemLine key={item.id}>
								<span>{t("support.staff.detail.thread.received")}</span>
							</TicketSystemLine>
						);
					case "status":
						return (
							<TicketSystemLine key={item.id}>
								<span>{t("support.staff.detail.thread.statusChanged")}</span>
								<StaffTicketStatusPill status={item.to} />
							</TicketSystemLine>
						);
				}
			})}
		</div>
	);
};
